import { useEffect, useRef, useState } from 'react'

import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { CreateEmailTemplateButton } from '@/components/CreateEmailTemplateButton'
import { EmailTemplateSelectItems } from '@/components/EmailTemplateSelectItems'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useEmailTemplates } from '@/models/emailTemplate'

// Mirrors the backend's SUPPORTED_TOKENS (insure/services/sequences/render.py).
const VARIABLES = [
  'lead.first_name',
  'lead.contact_name',
  'lead.company',
  'lead.email',
  'lead.phone',
  'lead.dot_number',
  'rep.first_name',
  'rep.name',
  'rep.email',
]

interface EmailStepEditorProps {
  subject: string
  body: string
  onChange: (next: { subject: string; body: string }) => void
  onSave: () => void
  onCancel: () => void
  saving?: boolean
  saveLabel?: string
  // New (empty) step: prefill from the first template (no-op when the rep has
  // no templates yet — the step starts blank).
  prefill?: boolean
}

export function EmailStepEditor({
  subject,
  body,
  onChange,
  onSave,
  onCancel,
  saving,
  saveLabel = 'Add step',
  prefill,
}: EmailStepEditorProps) {
  const { data: templates = [], isSuccess: templatesLoaded } = useEmailTemplates()
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  // MDXEditor only re-reads its markdown when remounted, so bump this whenever we
  // change the body programmatically (template apply / variable insert).
  const [editorKey, setEditorKey] = useState(0)
  const prefilled = useRef(false)

  function applyTemplate(id: string) {
    const t = templates.find((x) => x.id === id)
    if (!t) return
    setSelectedTemplateId(id)
    onChange({ subject: t.subject, body: t.body })
    setEditorKey((k) => k + 1)
  }

  // Prefill a new step once templates have loaded, as long as the rep hasn't
  // started typing. Templates come sorted default-first, so [0] is the right pick.
  useEffect(() => {
    if (!prefill || prefilled.current || !templatesLoaded) return
    if (subject !== '' || body !== '') return
    prefilled.current = true
    if (templates.length > 0) {
      applyTemplate(templates[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill, templatesLoaded])

  function insertVariable(v: string) {
    onChange({ subject, body: `${body}{{${v}}}` })
    setEditorKey((k) => k + 1)
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div>
        <label className="mb-1 block text-sm font-medium">Template</label>
        {templates.length > 0 ? (
          <Select value={selectedTemplateId ?? undefined} onValueChange={applyTemplate}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Start from a template" />
            </SelectTrigger>
            <SelectContent>
              <EmailTemplateSelectItems templates={templates} />
            </SelectContent>
          </Select>
        ) : (
          <CreateEmailTemplateButton />
        )}
      </div>

      <Input
        placeholder="Subject"
        value={subject}
        onChange={(e) => onChange({ subject: e.target.value, body })}
      />

      <div className="flex flex-wrap gap-1">
        {VARIABLES.map((v) => (
          <Button key={v} type="button" variant="outline" size="xs" onClick={() => insertVariable(v)}>
            {`{{${v}}}`}
          </Button>
        ))}
      </div>

      <RichTextareaWithSubmit
        editorKey={editorKey}
        value={body}
        onValueChange={(v) => onChange({ subject, body: v })}
        onSubmitMessage={() => onSave()}
        placeholder="Compose email… use variables like {{lead.first_name}}"
        buttonProps={{ disabled: saving, 'aria-label': saveLabel }}
        addonChildren={
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        }
      />
    </div>
  )
}
